<template>
  <v-card class="w-100 d-flex flex-column ga-4 pa-8">
    <v-sheet class="header-section">
      <h3 class="title-section">
        Probabilidade de conversões por curso
        <v-tooltip location="top">
          <template #activator="{ props }">
            <label class="empty-data" v-if="!internalData.success" v-bind="props">
              Dados insuficientes
              <v-icon size="14" class="summary-info" color="grey">
                mdi-help-circle
              </v-icon>
            </label>
          </template>

          Filtro não retornou dados.
        </v-tooltip>
      </h3>
      <p class="subtitle-section">
        Média geral da probabilidade de conversão por curso atribuída aos registros na última atualização.
      </p>
    </v-sheet>

    <rb-loader v-if="isLoading" v-model="internalData" :contained="true" />

    <FilterComponent
      v-model:view="mode"
      v-model:filters="filtersState"
      v-model:search="search"
      :available-ids="availableIdsByCategory"
      :show-chart="true"
      :show-table="true"
      :show-search="true"
      :show-filter="true"
      :show-refresh="true"
      :filters="filters"
      :enableProcessoSeletivo="false"
      :enableModalidade="true"
      :enableNivelEnsino="true"
      :enableCurso="true"
      :enableOfertaCurso="false"
      :enableEtapa="false"
      :enableFaixaConversao="false"
      :show-download="mode === 'table' && rows && rows.length > 0"
      :download-disabled="!rows || rows.length === 0"
      @click:download="openDownloadCsvPopup"
      @click:chart="mode = 'chart'"
      @click:table="mode = 'table'"
      @update:filters="applyFilters($event)"
      @submit:search="handleSearchSubmit"
      @click:refresh="reloadData()"
    />

    <v-sheet v-if="mode === 'chart'" class="w-100 d-flex">
      <div style="flex: 1 1 60%; height: 460px;">
        <HorizontalBarScroll
          :height="460"
          :rows="barRows"
          :visible-count="7"
          :color-range="['#EDF5F1', '#0EE378', '#000D1C']"
          :wrap-label="(v) => wrapAxisLabel(v, 22, 2)"
          @bar-click="handleBarClick"
        />
      </div>
    </v-sheet>

    <v-sheet v-if="mode === 'table'" class="w-100 d-flex">
      <TableInsights
        :headers="tableHeaders"
        :items="rows"
        :show-total="true"
        total-title="Total geral/Média geral"
        :total-col-span="3"
        :totals="tableTotals"
        v-model:page="page"
        v-model:itemsPerPage="ipp"
        :items-per-page-options="[5,10,25,50]"
        :highlight-index="1"
        @row:click="handleRowClick($event)"
      >
        <template #tooltip-content>
          Indica os cursos que apresentam as maiores médias de <strong>probabilidade de conversão</strong>, calculadas a partir do total de inscrições e do comportamento dos candidatos em cada oferta.
        </template>
        <template #cell:lt60="{ value }">
          <div class="d-flex align-center justify-end ga-1">
            <span>{{ value }}</span>
            <v-icon
              v-if="pctToNumber(value) >= 15 && pctToNumber(value) < 20"
              size="16"
              color="#FF8A00"
            >mdi-alert-circle</v-icon>
          </div>
        </template>

        <template #total:lt60="{ value }">{{ value }}</template>
        <template #total:between60_70="{ value }">{{ value }}</template>
        <template #total:between70_90="{ value }">{{ value }}</template>
        <template #total:gt90="{ value }">{{ value }}</template>
      </TableInsights>
    </v-sheet>

    <DetailPopUp
      v-model="detailPopUpVisible"
      :payload="payloadDetails"
      :url="urlDetailPopUp"
      :title-pop-up="detailPopUpTitle"
      subtitle-pop-up="Encontre o contato para realizar análise qualitativas"
      :headers="[
        { title:'Probabilidade', key:'probabilidade', align:'start' },
        { title:'Nome', key:'nome' },
        { title:'Processo', key:'processoNome' },
        { title:'Curso', key:'curso'}
      ]"
      :rows-keys="['id', 'nome', 'processoNome', 'curso', 'probabilidade']"
      @after-leave="detailPopUpVisible=false"
    />

    <RbPopUp
      v-model="downloadCsvPopupVisible"
      :width="400"
      confirm-text="Download"
      cancel-text="Cancelar"
      confirm-color="primary-60"
      variant-confirm="elevated"
      variant-cancel="outlined"
      :disabled-confirm="!rows || !rows.length"
      @confirm="downloadTableCsv"
    >
      <template #title>Download do relatório (CSV)</template>

      <template #message>
        Realize o download em CSV do widget<br />
        "<strong>Probabilidade de conversões por curso</strong>".
      </template>

      <template #confirm-icon>
        <v-icon size="18">mdi-download</v-icon>
      </template>
    </RbPopUp>
  </v-card>
</template>

<script>
import RbLoader from '@/ai_studio/components/common/RbLoader.vue'
import FilterComponent from './FilterComponent.vue'
import TableInsights from './TableInsights.vue'
import DetailPopUp from './detailsPopUp/DetailPopUp.vue'
import HorizontalBarScroll from './charts/HorizontalBar.vue'
import { downloadCsv } from '@/ai_studio/utils/exportCsv'
import RbPopUp from '@/ai_studio/components/common/RBPopup.vue'
import { PostWithJson } from '@/ai_studio/http/http'
import { calculateAvailableIds } from '@/ai_studio/utils/entityHelper'

export default {
  name: 'ConversionByStepCourse',
  components: {
    RbLoader,
    FilterComponent,
    TableInsights,
    DetailPopUp,
    HorizontalBarScroll,
    RbPopUp
  },

  props: {
    data: { type: Object, default: () => ({}) },
    topN: { type: Number, default: 6 },
    filters: { type: Object, default: () => ({}) }
  },

  data() {
    return {
      mode: 'chart',
      page: 1,
      ipp: 5,
      search: '',
      internalData: this.data,
      filtersState: this.filters || {},
      originalAvailableIds: null,
      isLoading: false,
      detailPopUpTitle: '',
      urlDetailPopUp: '',
      payloadDetails: {},
      detailPopUpVisible: false,
      downloadCsvPopupVisible: false,
    }
  },

  computed: {
    sourceData() {
      return Array.isArray(this.internalData?.dados)
        ? this.internalData.dados
        : Array.isArray(this.internalData)
          ? this.internalData
          : []
    },

    rows() {
      return this.sourceData.map(item => ({
        process: item.processo,
        idProcesso: item.idProcesso,
        curso: item.curso,
        idCurso: item.idCurso,
        idLocalOferta: item.idLocalOferta,
        unidade: item.unidade,
        local: item.local_oferta,
        modalidade: item.modalidade,
        idModalidade: item.idModalidade,
        nivel_de_ensino: item.nivel_de_ensino,
        idNivelDeEnsino: item.idNivelDeEnsino,
        inscricoes: Number(item.inscricoes) || 0,
        probabilidade: `${(Number(item.media) || 0).toFixed(2)}%`
      }))
    },

    barRows() {
      const items = this.sourceData
      if (!Array.isArray(items) || !items.length) return []

      const byCourse = new Map()

      for (const row of items) {
        const idCurso = row?.idCurso
        if (idCurso == null) continue

        const media = Number(row?.media)
        const inscr = Number(row?.inscricoes)

        if (!Number.isFinite(media) || !Number.isFinite(inscr) || inscr <= 0) continue

        const curso = row?.curso || 'Sem curso definido'
        const modalidade = row?.modalidade ? ` (${row.modalidade})` : ''
        const label = `${curso}${modalidade}`

        if (!byCourse.has(idCurso)) {
          byCourse.set(idCurso, {
            idCurso,
            label,
            wsum: 0,
            w: 0,
            inscricoes: 0,
            locais: new Set()
          })
        }

        const acc = byCourse.get(idCurso)

        acc.wsum += media * inscr
        acc.w += inscr
        acc.inscricoes += inscr

        if (row?.local_oferta) {
          acc.locais.add(row.local_oferta)
        }
      }

      const aggregated = Array.from(byCourse.values()).map(acc => {
        const avg = acc.w > 0 ? (acc.wsum / acc.w) : 0
        const prob = Math.max(0, Math.min(100, avg))

        return {
          idCidade: String(acc.idCurso),
          cidade: acc.label,
          inscricoes: acc.inscricoes,
          probabilidade: prob,

          meta: {
            idCurso: acc.idCurso,
            locais: Array.from(acc.locais)
          }
        }
      })
      return aggregated.sort((a, b) => b.inscricoes - a.inscricoes)
    },

    tableTotals() {
      const stats = this.sourceData.reduce((acc, item) => {
        const inscricoes = Number(item.inscricoes) || 0
        const media = Number(item.media) || 0

        acc.totalInscricoes += inscricoes
        acc.weightedMediaSum += (media * inscricoes)
        return acc
      }, { totalInscricoes: 0, weightedMediaSum: 0 })

      const mediaGeral = (stats.totalInscricoes > 0)
        ? (stats.weightedMediaSum / stats.totalInscricoes)
        : 0

      return {
        inscricoes: stats.totalInscricoes,
        probabilidade: `${mediaGeral.toFixed(2)}%`
      }
    },

    availableIdsByCategory() {
      if (this.originalAvailableIds) {
        return this.originalAvailableIds;
      }
      return calculateAvailableIds(this.sourceData);
    },
    tableHeaders() {
      return [
        { title:'Processo', key:'process', align:'start' },
        { title:'Curso', key:'curso' },
        { title:'Unidade', key:'unidade' },
        { title:'Local de Oferta', key:'local' },
        { title:'Modalidade', key:'modalidade' },
        { title:'Nivel de ensino', key:'nivel_de_ensino' },
        { title:'Inscrições', key:'inscricoes', align:'start' },
        { title:'Probabilidade', key:'probabilidade', align:'start' },
      ]
    },
  },

  watch: {
    filters: {
      deep: true,
      immediate: true,
      handler(v) {
        this.filtersState = v || {}
      }
    },
    data: {
      deep: true,
      immediate: true,
      handler(newVal) {
        this.internalData = newVal
        if (newVal && !this.originalAvailableIds) {
          const ids = calculateAvailableIds(newVal);
          if (Object.keys(ids).length > 0) {
            this.originalAvailableIds = ids;
          }
        }
      }
    }
  },

  methods: {
    openDownloadCsvPopup() {
      if (!this.rows || !this.rows.length) return
      this.downloadCsvPopupVisible = true
    },

    downloadTableCsv() {
      const fields = this.tableHeaders.map(header => header.title)

      const data = (this.rows || []).map(row => ([
        row.process ?? '',
        row.curso ?? '',
        row.unidade ?? '',
        row.local ?? '',
        row.modalidade ?? '',
        row.nivel_de_ensino ?? '',
        Number(row.inscricoes || 0).toLocaleString('pt-BR'),
        String(row.probabilidade ?? '')
      ]))

      if (this.tableTotals) {
        const totalInscricoes = Number(this.tableTotals.inscricoes || 0).toLocaleString('pt-BR')
        const totalProbabilidades = String(this.tableTotals.probabilidade ?? '')

        data.push([
          'Resumo dos dados',
          '',
          '',
          '',
          '',
          totalInscricoes,
          totalProbabilidades
        ])
      }

      const today = new Date().toISOString().slice(0, 10)
      downloadCsv({
        filename: `probabilidade_conversoes_por_curso_${today}.csv`,
        fields,
        data,
        delimiter: ';'
      })
    },
    abrirDrawerFiltros() {},

    wrapAxisLabel(value, maxCharsPerLine = 22, maxLines = 2) {
      const text = String(value ?? '')
      if (!text) return ''

      const words = text.split(' ')
      const lines = []
      let line = ''

      for (const w of words) {
        const next = line ? `${line} ${w}` : w

        if (next.length <= maxCharsPerLine) {
          line = next
        } else {
          if (line) lines.push(line)
          line = w

          if (line.length > maxCharsPerLine) {
            lines.push(line.slice(0, maxCharsPerLine))
            line = line.slice(maxCharsPerLine)
          }
        }

        if (lines.length === maxLines) break
      }

      if (lines.length < maxLines && line) lines.push(line)
      return lines.slice(0, maxLines).join('\n')
    },

    handleRowClick(row) {
      this.detailPopUpTitle = `${row.curso}`
      this.urlDetailPopUp = 'insights/getReportForecastByCourse'
      this.payloadDetails = {
        process: row.idProcesso,
        course: row.idCurso,
        local: row.idLocalOferta,
        education_level: row.idNivelDeEnsino,
        modality: row.idModalidade,
      }
      this.detailPopUpVisible = true
    },

    handleBarClick(d) {
      const meta = d?.meta || {}
      const curso = d?.cidade || 'Curso'

      this.detailPopUpTitle = `${curso}`
      this.urlDetailPopUp = 'insights/getReportForecastByCourse'
      this.payloadDetails = {
        course: meta.idCurso,
        local: meta.idLocalOferta
      }
      this.detailPopUpVisible = true
    },

    pctToNumber(value) {
      if (typeof value === 'string') return parseFloat(value.replace('%', '')) || 0
      return Number(value) || 0
    },

    async loadData(payload) {
      this.isLoading = true
      const url = 'insights/getReportForCourse'

      try {
        const newData = await PostWithJson(url, JSON.stringify(payload))
        if (newData) {
          this.internalData = newData;
          this.page = 1;
          if (!this.originalAvailableIds) {
            this.originalAvailableIds = calculateAvailableIds(newData);
          }
        }
      } catch (error) {
        console.error('Erro ao buscar dados com pesquisa:', error)
      } finally {
        this.isLoading = false
      }
    },

    handleSearchSubmit(searchValue) {
      this.loadData({ search: searchValue })
    },

    reloadData() {
      this.originalAvailableIds = null
      this.applyFilters(this.filtersState)
    },

    async applyFilters(newFilters) {
      if (newFilters) this.filtersState = newFilters

      const filtersBody = {
        process: this.filtersState.processo,
        education_level: this.filtersState.nivelEnsino,
        modality: this.filtersState.modalidade,
        course: this.filtersState.curso
      }

      await this.loadData(filtersBody)
    }
  }
}
</script>

<style>
.header-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  gap: 8px;
  align-self: stretch;
}
.title-section {
  color: var(--Text-Neutral-Primary, #191C1C);
  font-family: Roboto, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
}
.subtitle-section {
  color: var(--Text-Neutral-Primary, #191C1C);
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
}
</style>
